import React, { useState, useRef } from 'react';
import Select from '../../../components/ui/Select';
import { useTranslation } from 'react-i18next';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import Badge from '../../../components/ui/Badge';
import { scheduleService } from '../../../services/scheduleService';
import PaperAssignmentModal from './PaperAssignmentModal';

const SESSION_TYPE_VALUES = [
  'paper', 'keynote', 'invited', 'plenary', 'panel', 'workshop', 'tutorial',
  'poster', 'break', 'social', 'opening', 'closing', 'other',
];

const TYPE_COLORS = {
  paper: 'bg-primary-subtle text-primary border-primary/30',
  keynote: 'bg-accent-subtle text-accent border-accent/30',
  invited: 'bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-950/50 dark:text-violet-300 dark:border-violet-800',
  plenary: 'bg-primary-subtle text-primary border-primary/30',
  panel: 'bg-muted text-foreground border-border',
  break: 'bg-warning-subtle text-warning border-warning-border',
  social: 'bg-pink-50 text-pink-700 border-pink-200 dark:bg-pink-950/50 dark:text-pink-300 dark:border-pink-800',
  opening: 'bg-success-subtle text-success border-success-border',
  closing: 'bg-success-subtle text-success border-success-border',
  workshop: 'bg-secondary text-secondary-foreground border-secondary',
  tutorial: 'bg-cyan-50 text-cyan-700 border-cyan-200 dark:bg-cyan-950/50 dark:text-cyan-300 dark:border-cyan-800',
  poster: 'bg-lime-50 text-lime-700 border-lime-200 dark:bg-lime-950/50 dark:text-lime-300 dark:border-lime-800',
  other: 'bg-muted text-foreground border-border',
};

const SessionBuilder = ({ conferenceId, conference, sessions, rooms, tracks, conferenceDays, onRefresh }) => {
  const { t } = useTranslation('conferences');
  const [selectedDay, setSelectedDay] = useState(conferenceDays?.[0]?.date || '');
  const [showForm, setShowForm] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [showSpeakerForm, setShowSpeakerForm] = useState(null);
  const [assignModalSession, setAssignModalSession] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    title: '', sessionType: 'paper', roomId: '', trackId: '', startTime: '09:00', endTime: '10:30', chairName: '', description: '',
  });

  const [speakerForm, setSpeakerForm] = useState({
    customTitle: '', customSpeaker: '', customAffiliation: '', durationMinutes: '30',
  });

  // Drag state
  const dragItem = useRef(null);
  const dragSourceSession = useRef(null);
  const [dragOverSession, setDragOverSession] = useState(null);

  const daySessions = sessions
    ?.filter(s => s?.sessionDate === selectedDay)
    ?.sort((a, b) => (a?.startTime || '')?.localeCompare(b?.startTime || ''));

  const resetForm = () => {
    setForm({ title: '', sessionType: 'paper', roomId: '', trackId: '', startTime: '09:00', endTime: '10:30', chairName: '', description: '' });
    setShowForm(false);
    setEditingSession(null);
    setError('');
  };

  const handleEditSession = (session) => {
    setEditingSession(session?.id);
    setForm({
      title: session?.title || '',
      sessionType: session?.sessionType || 'paper',
      roomId: session?.roomId || '',
      trackId: session?.trackId || '',
      startTime: session?.startTime?.substring(0, 5) || '09:00',
      endTime: session?.endTime?.substring(0, 5) || '10:30',
      chairName: session?.chairName || '',
      description: session?.description || '',
    });
    setShowForm(true);
  };

  const handleSaveSession = async () => {
    if (!form.title?.trim()) { setError(t('sessionBuilder.errors.titleRequired')); return; }
    if (!form.startTime || !form.endTime) { setError(t('sessionBuilder.errors.timeRequired')); return; }
    try {
      setSaving(true);
      setError('');
      const sessionData = {
        title: form.title?.trim(),
        sessionType: form.sessionType,
        roomId: form.roomId || null,
        trackId: form.trackId || null,
        sessionDate: selectedDay,
        startTime: form.startTime,
        endTime: form.endTime,
        chairName: form.chairName?.trim() || null,
        description: form.description?.trim() || null,
      };
      if (editingSession) {
        await scheduleService?.updateSession(editingSession, sessionData);
      } else {
        await scheduleService?.createSession(conferenceId, sessionData);
      }
      resetForm();
      onRefresh?.();
    } catch (err) {
      setError(err?.message || t('sessionBuilder.errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm(t('sessionBuilder.confirmDelete'))) return;
    try {
      await scheduleService?.deleteSession(sessionId);
      onRefresh?.();
    } catch (err) {
      setError(err?.message || t('sessionBuilder.errors.deleteFailed'));
    }
  };

  const handleTogglePublish = async (session) => {
    try {
      await scheduleService?.updateSession(session?.id, { isPublished: !session?.isPublished });
      onRefresh?.();
    } catch (err) {
      setError(err?.message || t('sessionBuilder.errors.togglePublishFailed'));
    }
  };

  const handleRemoveItem = async (itemId) => {
    try {
      await scheduleService?.removeItem(itemId);
      onRefresh?.();
    } catch (err) {
      setError(err?.message || t('sessionBuilder.errors.removeFailed'));
    }
  };

  const handleAddSpeaker = async (sessionId) => {
    if (!speakerForm.customTitle?.trim()) return;
    try {
      setSaving(true);
      const session = sessions?.find(s => s?.id === sessionId);
      await scheduleService?.addCustomItem(sessionId, {
        customTitle: speakerForm.customTitle?.trim(),
        customSpeaker: speakerForm.customSpeaker?.trim() || null,
        customAffiliation: speakerForm.customAffiliation?.trim() || null,
        durationMinutes: parseInt(speakerForm.durationMinutes) || 30,
        presentationOrder: session?.items?.length || 0,
      });
      setSpeakerForm({ customTitle: '', customSpeaker: '', customAffiliation: '', durationMinutes: '30' });
      setShowSpeakerForm(null);
      onRefresh?.();
    } catch (err) {
      setError(err?.message || t('sessionBuilder.errors.addSpeakerFailed'));
    } finally {
      setSaving(false);
    }
  };

  // ---- Drag and Drop ----
  const handleDragStart = (e, item, sessionId) => {
    dragItem.current = item;
    dragSourceSession.current = sessionId;
    e.dataTransfer.effectAllowed = 'move';
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    dragItem.current = null;
    dragSourceSession.current = null;
    setDragOverSession(null);
  };

  const handleDragOver = (e, sessionId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverSession(sessionId);
  };

  const handleDragLeave = () => {
    setDragOverSession(null);
  };

  const handleDrop = async (e, targetSessionId) => {
    e.preventDefault();
    setDragOverSession(null);
    const item = dragItem.current;
    const sourceSessionId = dragSourceSession.current;
    if (!item || !sourceSessionId) return;

    try {
      if (sourceSessionId === targetSessionId) {
        // Reorder within same session — just refresh for now
        return;
      }
      // Move to different session
      const targetSession = sessions?.find(s => s?.id === targetSessionId);
      const newOrder = targetSession?.items?.length || 0;
      await scheduleService?.moveItem(item?.id, targetSessionId, newOrder);
      onRefresh?.();
    } catch (err) {
      console.error('Drop error:', err);
    }
  };

  const formatTime = (t) => t?.substring(0, 5);

  return (
    <div className="space-y-4">
      {/* Day selector */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {conferenceDays?.map(day => (
          <button
            key={day.date}
            onClick={() => setSelectedDay(day.date)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-250 ${
              selectedDay === day.date
                ? 'bg-primary text-primary-foreground'
                : 'bg-card border border-border text-foreground hover:bg-muted'
            }`}
          >
            {t('sessionBuilder.dayLabel', { number: day.dayNumber, label: day.label })}
          </button>
        ))}
      </div>

      {error && (
        <div className="bg-error-subtle border border-error-border text-error px-3 py-2 rounded-md text-sm flex items-center gap-2">
          <Icon name="AlertCircle" size={14} />
          {error}
          <button onClick={() => setError('')} className="ml-auto"><Icon name="X" size={14} /></button>
        </div>
      )}

      {/* Add session button */}
      {!showForm && (
        <Button size="sm" variant="outline" iconName="Plus" iconPosition="left" onClick={() => setShowForm(true)}>
          {t('sessionBuilder.addSession')}
        </Button>
      )}

      {/* Session form */}
      {showForm && (
        <div className="bg-card border border-border rounded-lg p-4 space-y-3">
          <h4 className="text-xs font-semibold text-foreground uppercase tracking-wide">
            {editingSession ? t('sessionBuilder.editSession') : t('sessionBuilder.newSession')}
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <Input label={t('sessionBuilder.fields.title')} required value={form.title} onChange={(e) => setForm({ ...form, title: e?.target?.value })} placeholder={t('sessionBuilder.placeholders.title')} />
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">{t('sessionBuilder.fields.type')}</label>
              <Select
                value={form.sessionType}
                onChange={(value) => setForm({ ...form, sessionType: value })}
                options={SESSION_TYPE_VALUES.map((v) => ({ value: v, label: t(`sessionBuilder.types.${v}`) }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">{t('sessionBuilder.fields.room')}</label>
              <Select
                value={form.roomId}
                onChange={(value) => setForm({ ...form, roomId: value })}
                placeholder={t('sessionBuilder.fields.noRoom')}
                clearable
                options={(rooms || []).map((r) => ({ value: r.id, label: r.name }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">{t('sessionBuilder.fields.track')}</label>
              <Select
                value={form.trackId}
                onChange={(value) => setForm({ ...form, trackId: value })}
                placeholder={t('sessionBuilder.fields.noTrack')}
                clearable
                options={(tracks || []).map((t2) => ({ value: t2.id, label: t2.name }))}
              />
            </div>
            <Input label={t('sessionBuilder.fields.startTime')} type="time" required value={form.startTime} onChange={(e) => setForm({ ...form, startTime: e?.target?.value })} />
            <Input label={t('sessionBuilder.fields.endTime')} type="time" required value={form.endTime} onChange={(e) => setForm({ ...form, endTime: e?.target?.value })} />
            <Input label={t('sessionBuilder.fields.chair')} value={form.chairName} onChange={(e) => setForm({ ...form, chairName: e?.target?.value })} placeholder={t('sessionBuilder.placeholders.chair')} />
            <div className="sm:col-span-2">
              <Input label={t('sessionBuilder.fields.description')} value={form.description} onChange={(e) => setForm({ ...form, description: e?.target?.value })} placeholder={t('sessionBuilder.placeholders.description')} />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={handleSaveSession} loading={saving}>
              {editingSession ? t('sessionBuilder.updateSession') : t('sessionBuilder.createSession')}
            </Button>
            <Button size="sm" variant="ghost" onClick={resetForm}>{t('sessionBuilder.cancel')}</Button>
          </div>
        </div>
      )}

      {/* Session list */}
      {daySessions?.length === 0 ? (
        <div className="text-center py-8 bg-card border border-border rounded-lg">
          <Icon name="Calendar" size={24} className="text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">{t('sessionBuilder.noSessions')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {daySessions?.map(session => (
            <div
              key={session?.id}
              className={`bg-card border rounded-lg overflow-hidden transition-colors ${
                dragOverSession === session?.id ? 'border-primary border-2 bg-primary/5' : 'border-border'
              }`}
              onDragOver={(e) => handleDragOver(e, session?.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, session?.id)}
            >
              {/* Session header */}
              <div className="px-4 py-3 border-b border-border bg-muted/30 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="text-sm font-semibold text-foreground truncate">{session?.title}</span>
                      {session?.isPublished && (
                        <Icon name="Globe" size={12} className="text-success flex-shrink-0" title={t('sessionBuilder.actions.published')} />
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="text-xs font-mono text-muted-foreground whitespace-nowrap">
                        {formatTime(session?.startTime)} – {formatTime(session?.endTime)}
                      </div>
                      {session?.room && (
                        <span className="text-xs px-2 py-0.5 bg-muted text-muted-foreground whitespace-nowrap">
                          {session?.room?.name}
                        </span>
                      )}
                      <span className={`text-xs px-2 py-0.5 border font-medium ${TYPE_COLORS[session?.sessionType] || TYPE_COLORS.other}`}>
                        {t(`sessionBuilder.types.${session?.sessionType}`, session?.sessionType)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0 flex-wrap justify-end">
                    <Button size="xs" variant="ghost" iconName="Plus" onClick={() => setAssignModalSession(session)} title={t('sessionBuilder.actions.addPaper')} />
                    <Button size="xs" variant="ghost" iconName="UserPlus" onClick={() => setShowSpeakerForm(session?.id)} title={t('sessionBuilder.actions.addSpeaker')} />
                    <Button size="xs" variant="ghost" iconName="Pencil" onClick={() => handleEditSession(session)} title={t('sessionBuilder.actions.edit')} />
                    <Button
                      size="xs"
                      variant="ghost"
                      iconName={session?.isPublished ? 'EyeOff' : 'Eye'}
                      onClick={() => handleTogglePublish(session)}
                      title={session?.isPublished ? t('sessionBuilder.actions.unpublish') : t('sessionBuilder.actions.publish')}
                    />
                    <Button size="xs" variant="ghost" iconName="Trash2" onClick={() => handleDeleteSession(session?.id)} title={t('sessionBuilder.actions.delete')} />
                  </div>
                </div>
              </div>

              {session?.chairName && (
                <div className="px-4 py-1.5 text-xs text-muted-foreground border-b border-border">
                  {t('sessionBuilder.chairPrefix')}: {session?.chairName}
                </div>
              )}

              {/* Add Speaker inline form */}
              {showSpeakerForm === session?.id && (
                <div className="px-4 py-3 border-b border-border bg-violet-50/30 space-y-2">
                  <h5 className="text-xs font-semibold text-foreground">{t('sessionBuilder.addSpeakerTitle')}</h5>
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                    <Input
                      placeholder={t('sessionBuilder.placeholders.talkTitle')}
                      value={speakerForm.customTitle}
                      onChange={(e) => setSpeakerForm({ ...speakerForm, customTitle: e?.target?.value })}
                    />
                    <Input
                      placeholder={t('sessionBuilder.placeholders.speakerName')}
                      value={speakerForm.customSpeaker}
                      onChange={(e) => setSpeakerForm({ ...speakerForm, customSpeaker: e?.target?.value })}
                    />
                    <Input
                      placeholder={t('sessionBuilder.placeholders.affiliation')}
                      value={speakerForm.customAffiliation}
                      onChange={(e) => setSpeakerForm({ ...speakerForm, customAffiliation: e?.target?.value })}
                    />
                    <Input
                      placeholder={t('sessionBuilder.placeholders.durationMin')}
                      type="number"
                      value={speakerForm.durationMinutes}
                      onChange={(e) => setSpeakerForm({ ...speakerForm, durationMinutes: e?.target?.value })}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button size="xs" onClick={() => handleAddSpeaker(session?.id)} loading={saving}>{t('sessionBuilder.add')}</Button>
                    <Button size="xs" variant="ghost" onClick={() => setShowSpeakerForm(null)}>{t('sessionBuilder.cancel')}</Button>
                  </div>
                </div>
              )}

              {/* Items list */}
              <div className="divide-y divide-border">
                {session?.items?.length === 0 ? (
                  <div className="px-4 py-4 text-center text-xs text-muted-foreground">
                    {t('sessionBuilder.noItems')}
                  </div>
                ) : (
                  session?.items?.map((item, idx) => (
                    <div
                      key={item?.id}
                      className="px-3 sm:px-4 py-2.5 flex items-start sm:items-center gap-2 sm:gap-3 hover:bg-muted/20 transition-colors cursor-grab active:cursor-grabbing"
                      draggable
                      onDragStart={(e) => handleDragStart(e, item, session?.id)}
                      onDragEnd={handleDragEnd}
                    >
                      {/* Drag handle */}
                      <Icon name="GripVertical" size={14} className="text-muted-foreground/50 flex-shrink-0 mt-1 sm:mt-0" />

                      {/* Order number */}
                      <span className="text-xs text-muted-foreground font-mono w-5 flex-shrink-0 text-right mt-0.5 sm:mt-0">
                        {idx + 1}.
                      </span>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        {item?.itemType === 'paper' && item?.paper ? (
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs font-mono text-primary font-semibold flex-shrink-0">
                                #{item?.paper?.paperNumber}
                              </span>
                              <span className="text-sm font-medium text-foreground line-clamp-2 sm:truncate">
                                {item?.paper?.title}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5 truncate">
                              {item?.paper?.author?.firstName} {item?.paper?.author?.lastName}
                              {item?.paper?.author?.institution && ` — ${item?.paper?.author?.institution}`}
                            </p>
                            <span className="text-xs text-muted-foreground sm:hidden">{item?.durationMinutes} {t('sessionBuilder.minutes')}</span>
                          </div>
                        ) : (
                          <div>
                            <div className="flex items-center gap-2">
                              <Icon name="Mic" size={12} className="text-violet-600 flex-shrink-0" />
                              <span className="text-sm font-medium text-foreground line-clamp-2 sm:truncate">
                                {item?.customTitle}
                              </span>
                            </div>
                            {item?.customSpeaker && (
                              <p className="text-xs text-muted-foreground mt-0.5 truncate">
                                {item?.customSpeaker}
                                {item?.customAffiliation && `, ${item?.customAffiliation}`}
                              </p>
                            )}
                            <span className="text-xs text-muted-foreground sm:hidden">{item?.durationMinutes} {t('sessionBuilder.minutes')}</span>
                          </div>
                        )}
                      </div>

                      {/* Duration - hidden on mobile, shown inline in content above */}
                      <span className="text-xs text-muted-foreground flex-shrink-0 hidden sm:block">
                        {item?.durationMinutes} {t('sessionBuilder.minutes')}
                      </span>

                      {/* Remove */}
                      <button
                        onClick={() => handleRemoveItem(item?.id)}
                        className="p-1 hover:bg-error-subtle transition-colors flex-shrink-0"
                        title={t('sessionBuilder.actions.remove')}
                      >
                        <Icon name="X" size={14} className="text-muted-foreground hover:text-error" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Paper Assignment Modal */}
      {assignModalSession && (
        <PaperAssignmentModal
          conferenceId={conferenceId}
          session={assignModalSession}
          tracks={tracks}
          onClose={() => setAssignModalSession(null)}
          onAssigned={onRefresh}
        />
      )}
    </div>
  );
};

export default SessionBuilder;
